import { useEffect, useState } from "react";
import { useNavigate, createSearchParams, useLocation } from "react-router-dom";
import PaginationComponent from "../../components/Pagination/PaginationComponent";
import { OfferServiceService } from "../../services/offerServiceService";
import { Spin } from "antd";

import NoImageAvailable from "../../assets/images/no-image-available.jpg";

function statusBadge(promo) {
  const now = Date.now();
  const start = promo.applyStartDate
    ? new Date(promo.applyStartDate).getTime()
    : null;
  const end = promo.applyEndDate
    ? new Date(promo.applyEndDate).getTime()
    : null;

  if (end && now > end)
    return { text: "Đã kết thúc", cls: "bg-gray-200 text-gray-700" };
  if (start && now < start)
    return { text: "Sắp diễn ra", cls: "bg-blue-100 text-blue-700" };
  return { text: "Đang diễn ra", cls: "bg-green-100 text-green-700" };
}

export default function ServicesOffersListPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [list, setList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // phân trang
  const sp = new URLSearchParams(location.search);
  const initialPage = Number(sp.get("page") || 1);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const pageSize = 6;

  useEffect(() => {
    const params = createSearchParams({ page: String(currentPage) }).toString();
    navigate(
      { pathname: "/services-offers", search: `?${params}` },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const res = await OfferServiceService.getOfferServices({
        page: currentPage,
        pageSize,
      });
      const { totalRecord, listData } = res.Data;
      setList(listData ?? []);
      setTotalRecord(totalRecord ?? 0);
      setIsLoading(false);
    })();
  }, [currentPage]);

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <Spin spinning={isLoading}>
      <div className="w-full min-h-[60vh] px-4 lg:px-8 py-6 lg:py-10">
        <header className="mb-6 lg:mb-10">
          <h1 className="text-2xl lg:text-4xl font-bold">Dịch vụ & Ưu đãi</h1>
          <p className="text-sm lg:text-base text-gray-500 mt-1">
            Tổng hợp chương trình ưu đãi, dịch vụ tại Ly iPhone – cập nhật liên
            tục.
          </p>
        </header>

        {/* Grid cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {list.map((item) => {
            const badge = statusBadge(item);
            return (
              <div
                key={item.slug}
                className="flex flex-col bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
              >
                <div className="relative">
                  <img
                    src={item.images || NoImageAvailable}
                    alt={item.title}
                    className="w-full aspect-[16/10] object-cover"
                    loading="lazy"
                  />
                  <span
                    className={`absolute top-2 left-2 px-2 py-1 text-xs rounded-md ${badge.cls}`}
                  >
                    {badge.text}
                  </span>
                </div>

                <div className="p-4 lg:p-5 flex-1 flex flex-col justify-between">
                  <div className="flex-1 flex flex-col">
                    <h2 className="text-lg lg:text-xl font-semibold line-clamp-2">
                      {item.title}
                    </h2>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                      {item.excerpt}
                    </p>

                    {/* thời gian ngắn gọn */}
                    {(item.applyStartDate || item.applyEndDate) && (
                      <p className="text-xs text-gray-500 mt-3">
                        {item.applyStartDate && (
                          <>
                            Từ:{" "}
                            {new Date(item.applyStartDate).toLocaleDateString(
                              "vi-VN"
                            )}
                          </>
                        )}
                        {item.applyEndDate && (
                          <>
                            {" "}
                            • Đến:{" "}
                            {new Date(item.applyEndDate).toLocaleDateString(
                              "vi-VN"
                            )}
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="mt-4 lg:mt-5">
                    <button
                      onClick={() => navigate(`/services-offers/${item.slug}`)}
                      className="w-full lg:w-auto px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-100 transition"
                    >
                      Xem chi tiết
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {list.length === 0 && !isLoading && (
          <div className="text-center text-gray-500 py-16">
            Chưa có chương trình nào.
          </div>
        )}

        {/* Pagination */}
        {!isLoading && (
          <div className="mt-6 lg:mt-10 flex justify-center">
            <PaginationComponent
              currentPage={currentPage}
              pageSize={pageSize}
              total={totalRecord}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </Spin>
  );
}
